import { addMinutes, isAfter, isBefore, parseISO, subMinutes } from 'date-fns';
import type {
  ProgramDialInInformation,
  ProgramEventInviteeRole,
  ProgramEventStatus,
  ProgramEventType,
  ProgramUserRsvp,
} from 'models';
import type {
  ActivityDataObject,
  ActivityTodoDataObject,
  CommonTodoDataObject,
  EventTodoDataObject,
  PollDataObject,
  PollTodoDataObject,
  PostDataObject,
  PostTodoDataObject,
  ProgramEventDataObject,
  TodoDataObject,
  UserTodoDataObject,
  WithingsOrderPostDataObject,
  WithingsOrderPostTodoDataObject,
} from './service';

// TODO fix something is not required and is optional

export type ActivityTodo = {
  activity: Activity;
  type: 'Activity';
} & CommonTodo;

export type EventTodo = {
  event: ProgramEvent;
  type: 'Event';
} & CommonTodo;

export type PollTodo = {
  poll: Poll;
  type: 'Poll';
} & CommonTodo;

export type PostTodo = {
  post: Post;
  type: 'Post';
} & CommonTodo;

export type ProgramEvent = {
  bmgId: null | string;
  currentUserRole: ProgramEventInviteeRole;
  currentUserRsvp: ProgramUserRsvp;
  dialInInformation?: ProgramDialInInformation;
  endTime: Date;
  eventType: ProgramEventType;
  id: string;
  name: string;
  startTime: Date;
  status: ProgramEventStatus;
  textContent: string;
};

export type Todo = ActivityTodo | EventTodo | PollTodo | PostTodo | UserTodo | WithingsOrderPostTodo;

export type UserTodo = {
  type: 'User';
} & CommonTodo;

export type WithingsOrderPostTodo = {
  type: 'WithingsOrderPost';
  withingsOrderPost: WithingsOrderPost;
} & CommonTodo;

type Activity = {
  currentActivityTime?: Date;
  description: string;
  id: string;
  name: string;
  periodicityType: 'annually' | 'daily' | 'monthly' | 'one_time' | 'quarterly' | 'weekly';
  times: Array<{ id: string; time: string }>;
};

type CommonTodo = {
  endTime: Date;
  id: string;
  path: string;
  startTime: Date;
};

type Poll = {
  description: string;
  id: string;
  title: string;
};

type Post = {
  htmlContent: string;
  id: string;
  textContent: string;
  title: string;
};

type WithingsOrderPost = {
  id: string;
  textContent: string;
  title: string;
};

export const createTodo = (todo: TodoDataObject): Todo => {
  switch (todo.type) {
    case 'Activity':
      return createActivityTodo(todo);
    case 'Event':
      return createEventTodo(todo);
    case 'Poll':
      return createPollTodo(todo);
    case 'Post':
      return createPostTodo(todo);
    case 'User':
      return createUserTodo(todo);
    case 'WithingsOrderPost':
      return createWithingsOrderPostTodo(todo);
  }
};

const createCommonTodo = (todo: CommonTodoDataObject): CommonTodo => ({
  endTime: parseISO(todo.end_time),
  id: todo.id,
  path: todo.path,
  startTime: parseISO(todo.start_time),
});

export const createActivityTodo = (todo: ActivityTodoDataObject): ActivityTodo => ({
  ...createCommonTodo(todo),
  activity: createActivity(todo.activity),
  type: todo.type,
});

const createActivity = (activity: ActivityDataObject): Activity => ({
  currentActivityTime: activity.current_activity_time ? parseISO(activity.current_activity_time) : undefined,
  description: activity.description,
  id: activity.id,
  name: activity.name,
  periodicityType: activity.periodicity_type,
  times: activity.times,
});

export const createEventTodo = (todo: EventTodoDataObject): EventTodo => ({
  ...createCommonTodo(todo),
  event: createEvent(todo.event),
  type: todo.type,
});

const createEvent = (event: ProgramEventDataObject): ProgramEvent => ({
  bmgId: event.bmg_id,
  currentUserRole: event.current_user_role,
  currentUserRsvp: event.current_user_rsvp,
  dialInInformation: event.dial_in_information,
  endTime: parseISO(event.end_time),
  eventType: event.event_type,
  id: event.id,
  name: event.name,
  startTime: parseISO(event.start_time),
  status: event.status,
  textContent: event.text_content,
});

export const createPollTodo = (todo: PollTodoDataObject): PollTodo => ({
  ...createCommonTodo(todo),
  poll: createPoll(todo.poll),
  type: todo.type,
});

const createPoll = (poll: PollDataObject): Poll => ({
  description: poll.description,
  id: poll.id,
  title: poll.title,
});

export const createWithingsOrderPostTodo = (todo: WithingsOrderPostTodoDataObject): WithingsOrderPostTodo => ({
  ...createCommonTodo(todo),
  type: todo.type,
  withingsOrderPost: createWithingsOrderPost(todo.withings_order_post),
});

const createWithingsOrderPost = (withingsOrderPost: WithingsOrderPostDataObject): WithingsOrderPost => ({
  id: withingsOrderPost.id,
  textContent: withingsOrderPost.text_content,
  title: withingsOrderPost.title,
});

export const createPostTodo = (todo: PostTodoDataObject): PostTodo => ({
  ...createCommonTodo(todo),
  post: createPost(todo.post),
  type: todo.type,
});

const createPost = (post: PostDataObject): Post => ({
  htmlContent: post.html_content,
  id: post.id,
  textContent: post.text_content,
  title: post.title,
});

export const createUserTodo = (todo: UserTodoDataObject): UserTodo => ({
  ...createCommonTodo(todo),
  type: todo.type,
});

const activityOffset = 15;

const isAfterActivityAvailable = (time: Date) => {
  const endTime = addMinutes(time, activityOffset);
  return isAfter(new Date(), endTime);
};

const isBeforeActivityAvailable = (time: Date) => {
  const startTime = subMinutes(time, activityOffset);
  return isBefore(new Date(), startTime);
};

export const isActivityAvailable = (currentActivityTime: Date) =>
  !(isBeforeActivityAvailable(currentActivityTime) || isAfterActivityAvailable(currentActivityTime));
